import { useEffect, useState } from 'react';
import { useForm, type UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { fxCloseFormSchema, type FxCloseFormValues } from '../lib/fx-close-form-schema';
import { todayIsoDate } from '../lib/form-helpers';
import { useCloseFxHedge, type CloseFxHedgePayload } from '../api/fx-close';
import { extractExecutionApiMessage } from '../api/executions';
import type { HedgeBookEntry } from '../api/hedge-book';

export interface FxCloseDialogProps {
  entry: HedgeBookEntry;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function deriveDefaults(entry: HedgeBookEntry): FxCloseFormValues {
  return {
    closeQuantity: entry.openQuantityMt ?? entry.quantityMt,
    executedRate: '',
    valueDate: todayIsoDate(),
    contractReference: '',
    notes: '',
  };
}

function buildPayload(entry: HedgeBookEntry, values: FxCloseFormValues): CloseFxHedgePayload {
  const payload: CloseFxHedgePayload = {
    hedgeRequestId: entry.id,
    closeQuantity: values.closeQuantity,
    executedRate: values.executedRate,
    valueDate: values.valueDate,
  };
  const ref = values.contractReference?.trim();
  if (ref) payload.contractReference = ref;
  const notes = values.notes?.trim();
  if (notes) payload.notes = notes;
  return payload;
}

export function FxCloseDialog({ entry, open, onOpenChange }: Readonly<FxCloseDialogProps>) {
  const form = useForm<FxCloseFormValues>({
    resolver: zodResolver(fxCloseFormSchema),
    defaultValues: deriveDefaults(entry),
  });
  const mutation = useCloseFxHedge();
  useEffect(() => {
    if (open) form.reset(deriveDefaults(entry));
  }, [open, entry, form]);
  const submit = form.handleSubmit((values) => {
    mutation.mutate(buildPayload(entry, values), {
      onSuccess: () => {
        toast.success('FX hedge close recorded.');
        onOpenChange(false);
      },
      onError: (error) => {
        toast.error(extractExecutionApiMessage(error));
      },
    });
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="fx-close-dialog" className="sm:max-w-md">
        <Header entry={entry} />
        <DialogBody
          form={form}
          isPending={mutation.isPending}
          onCancel={() => {
            onOpenChange(false);
          }}
          onSubmit={submit}
        />
      </DialogContent>
    </Dialog>
  );
}

function Header({ entry }: Readonly<{ entry: HedgeBookEntry }>) {
  return (
    <DialogHeader>
      <DialogTitle>Close FX hedge</DialogTitle>
      <DialogDescription>
        {entry.currencyPair ?? '—'} · {entry.direction} · open {entry.openQuantityMt ?? '0'}
      </DialogDescription>
    </DialogHeader>
  );
}

interface DialogBodyProps {
  form: UseFormReturn<FxCloseFormValues>;
  isPending: boolean;
  onCancel: () => void;
  onSubmit: (event: React.BaseSyntheticEvent) => Promise<void>;
}

function DialogBody({ form, isPending, onCancel, onSubmit }: Readonly<DialogBodyProps>) {
  return (
    <Form {...form}>
      <form
        onSubmit={(event) => {
          void onSubmit(event);
        }}
        className="flex flex-col gap-4"
      >
        <Fields control={form.control} />
        <Footer isPending={isPending} onCancel={onCancel} />
      </form>
    </Form>
  );
}

type FxCtrl = UseFormReturn<FxCloseFormValues>['control'];

interface FxFieldSpec {
  name: keyof FxCloseFormValues;
  label: string;
  inputMode?: 'decimal' | 'text';
  type?: 'text' | 'date';
  testId?: string;
  span?: 'half' | 'full';
}

const FX_FIELDS: FxFieldSpec[] = [
  {
    name: 'closeQuantity',
    label: 'Close quantity',
    inputMode: 'decimal',
    testId: 'fx-close-quantity',
  },
  { name: 'executedRate', label: 'Executed rate', inputMode: 'decimal', testId: 'fx-close-rate' },
  { name: 'valueDate', label: 'Value date', type: 'date', testId: 'fx-close-value-date' },
  { name: 'contractReference', label: 'Contract reference' },
];

function FxTextField({ control, spec }: Readonly<{ control: FxCtrl; spec: FxFieldSpec }>) {
  return (
    <FormField
      control={control}
      name={spec.name}
      render={({ field }) => (
        <FormItem className={spec.span === 'full' ? 'sm:col-span-2' : undefined}>
          <FormLabel>{spec.label}</FormLabel>
          <FormControl>
            <Input
              type={spec.type ?? 'text'}
              inputMode={spec.inputMode}
              data-testid={spec.testId}
              {...field}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

function Fields({ control }: Readonly<{ control: FxCtrl }>) {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      {FX_FIELDS.map((spec) => (
        <FxTextField key={spec.name} control={control} spec={spec} />
      ))}
      <FormField
        control={control}
        name="notes"
        render={({ field }) => (
          <FormItem className="sm:col-span-2">
            <FormLabel>Notes</FormLabel>
            <FormControl>
              <Textarea rows={3} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
}

function Footer({ isPending, onCancel }: Readonly<{ isPending: boolean; onCancel: () => void }>) {
  return (
    <DialogFooter>
      <Button type="button" variant="ghost" onClick={onCancel}>
        Cancel
      </Button>
      <Button type="submit" disabled={isPending} data-testid="fx-close-submit">
        {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />}
        Close FX hedge
      </Button>
    </DialogFooter>
  );
}

export function useFxCloseDialogState() {
  const [openFor, setOpenFor] = useState<HedgeBookEntry | null>(null);
  return {
    openFor,
    open: (entry: HedgeBookEntry) => {
      setOpenFor(entry);
    },
    close: () => {
      setOpenFor(null);
    },
  };
}

export { buildPayload as buildFxClosePayload };
